import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ConditionalFormatProvider } from '../components/ConditionalFormatContext'
import { DataTableCard } from '../components/DataTableCard'
import { DataFreshness } from '../components/DataFreshness'
import { FormattedTableCell } from '../components/FormattedTableCell'
import { PageScrollLayout, PageTableStack } from '../components/PageScrollLayout'
import { nextSortDirection, SortableTableHeader, type SortDirection } from '../components/SortableTableHeader'
import { Api, ApiError, type BoxScoreRow, type CurrentRatingRow, type RemainingScheduleRow } from '../services/api'
import { MATCHUP_COLUMN_SCALES, MATCHUP_HEATMAP_COLUMNS } from '../utils/tableHeatmapScales'

const HOME_FIELD_ADVANTAGE = 0.32
const WIN_PROB_COEFFICIENT = 0.3884

/** Below this row count the table stays content-height; at/above it grows to the outlet bottom. */
const TABLE_GROW_ROW_THRESHOLD = 10

const REFETCH_KEYS: ReadonlyArray<readonly unknown[]> = [
  ['ratings'],
  ['remaining_schedule'],
  ['box_scores'],
]

const TEAM_ALIASES: Record<string, string> = {
  'Arizona Diamondbacks': 'Arizona',
  'Atlanta Braves': 'Atlanta',
  'Baltimore Orioles': 'Baltimore',
  'Boston Red Sox': 'Boston',
  'Chicago Cubs': 'Chicago Cubs',
  'Chicago White Sox': 'Chicago White Sox',
  'Cincinnati Reds': 'Cincinnati',
  'Cleveland Guardians': 'Cleveland',
  'Colorado Rockies': 'Colorado',
  'Detroit Tigers': 'Detroit',
  'Houston Astros': 'Houston',
  'Kansas City Royals': 'Kansas City',
  'Los Angeles Angels': 'LA Angels',
  'Los Angeles Dodgers': 'LA Dodgers',
  'Miami Marlins': 'Miami',
  'Milwaukee Brewers': 'Milwaukee',
  'Minnesota Twins': 'Minnesota',
  'New York Mets': 'NY Mets',
  'New York Yankees': 'NY Yankees',
  'Oakland Athletics': 'Oakland',
  Athletics: 'Oakland',
  "A's": 'Oakland',
  'Philadelphia Phillies': 'Philadelphia',
  'Pittsburgh Pirates': 'Pittsburgh',
  'San Diego Padres': 'San Diego',
  'San Francisco Giants': 'San Francisco',
  'Seattle Mariners': 'Seattle',
  'St. Louis Cardinals': 'St. Louis',
  'Tampa Bay Rays': 'Tampa Bay',
  'Texas Rangers': 'Texas',
  'Toronto Blue Jays': 'Toronto',
  'Washington Nationals': 'Washington',
}

interface MatchupRow {
  id: string
  date: string
  timeLabel: string
  away: string
  home: string
  awayRating: number
  homeRating: number
  awayProb: number
  homeProb: number
  awayOdds: number
  homeOdds: number
  favorite: string
  favoriteProb: number
  favoriteOdds: number
  spread: number
  status: string
  sortKey: string
}

type SortKey =
  | 'time'
  | 'matchup'
  | 'awayRating'
  | 'homeRating'
  | 'awayFairLine'
  | 'homeFairLine'
  | 'favorite'
  | 'status'

export function MatchupsPage() {
  const ratingsQuery = useQuery({
    queryKey: ['ratings'],
    queryFn: Api.ratings,
  })

  const scheduleQuery = useQuery({
    queryKey: ['remaining_schedule'],
    queryFn: Api.remainingSchedule,
  })

  const boxScoresQuery = useQuery({
    queryKey: ['box_scores'],
    queryFn: Api.boxScores,
  })

  const [selectedDate, setSelectedDate] = useState('')
  const [userPickedDate, setUserPickedDate] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>('time')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const allGames = useMemo(
    () => combineDailySchedule(boxScoresQuery.data ?? [], scheduleQuery.data ?? []),
    [boxScoresQuery.data, scheduleQuery.data],
  )

  const dateOptions = useMemo(() => {
    const dates = new Set<string>()
    for (const row of allGames) {
      const date = toYmd(row.Date)
      if (date) dates.add(date)
    }
    return [...dates].sort()
  }, [allGames])

  const scheduleDataReady = !scheduleQuery.isLoading && !boxScoresQuery.isLoading

  useEffect(() => {
    if (!scheduleDataReady || !dateOptions.length) return
    const today = localYmd(new Date())

    if (userPickedDate) {
      if (selectedDate && !dateOptions.includes(selectedDate)) {
        setSelectedDate(pickDefaultMatchupDate(dateOptions, today))
      }
      return
    }

    setSelectedDate(pickDefaultMatchupDate(dateOptions, today))
  }, [dateOptions, scheduleDataReady, userPickedDate, selectedDate])

  const ratingMap = useMemo(() => buildRatingMap(ratingsQuery.data ?? []), [ratingsQuery.data])

  const matchups = useMemo(() => {
    if (!selectedDate) return []
    const rows: MatchupRow[] = []
    for (const game of allGames) {
      const date = toYmd(game.Date)
      if (date !== selectedDate) continue
      const awayRating = ratingFor(game.Away_Team, ratingMap)
      const homeRating = ratingFor(game.Home_Team, ratingMap)
      if (!Number.isFinite(awayRating) || !Number.isFinite(homeRating)) continue

      const homeProb = homeWinProbability(homeRating, awayRating)
      const awayProb = 1 - homeProb
      const awayOdds = fairAmericanOdds(awayProb)
      const homeOdds = fairAmericanOdds(homeProb)
      const homeIsFavorite = homeProb >= awayProb
      const favoriteProb = Math.max(homeProb, awayProb)
      rows.push({
        id: String(game.GamePk ?? `${game.Date}-${game.Away_Team}-${game.Home_Team}`),
        date,
        timeLabel: formatGameTime(game.Scheduled_Time_UTC),
        away: String(game.Away_Team ?? ''),
        home: String(game.Home_Team ?? ''),
        awayRating,
        homeRating,
        awayProb,
        homeProb,
        awayOdds,
        homeOdds,
        favorite: homeIsFavorite ? game.Home_Team : game.Away_Team,
        favoriteProb,
        favoriteOdds: homeIsFavorite ? homeOdds : awayOdds,
        spread: Math.abs(homeRating - awayRating),
        status: statusLabel(game),
        sortKey: sortKeyForGame(game),
      })
    }
    return rows
  }, [allGames, ratingMap, selectedDate])

  const sortedMatchups = useMemo(
    () => sortMatchups(matchups, sortKey, sortDirection),
    [matchups, sortDirection, sortKey],
  )

  const setSort = (nextKey: SortKey) => {
    if (nextKey === sortKey) {
      setSortDirection(nextSortDirection)
      return
    }
    setSortKey(nextKey)
    setSortDirection(defaultSortDirection(nextKey))
  }

  const biggestFavorite = useMemo(
    () => (matchups.length ? [...matchups].sort((a, b) => b.favoriteProb - a.favoriteProb)[0] : undefined),
    [matchups],
  )
  const closestGame = useMemo(
    () => (matchups.length ? [...matchups].sort((a, b) => a.spread - b.spread)[0] : undefined),
    [matchups],
  )

  const isLoading = ratingsQuery.isLoading || scheduleQuery.isLoading || boxScoresQuery.isLoading
  const error = ratingsQuery.error ?? scheduleQuery.error ?? boxScoresQuery.error

  const getHeatmapValue = useCallback((row: MatchupRow, key: string) => {
    switch (key) {
      case 'awayRating':
        return row.awayRating
      case 'homeRating':
        return row.homeRating
      case 'awayProb':
        return row.awayProb
      case 'homeProb':
        return row.homeProb
      case 'favoriteProb':
        return row.favoriteProb
      default:
        return null
    }
  }, [])

  const datePicker = (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-slate-500">Date</span>
      <select
        className="rounded border border-slate-300 px-2 py-1"
        value={selectedDate}
        onChange={(e) => {
          setUserPickedDate(true)
          setSelectedDate(e.target.value)
        }}
        disabled={!dateOptions.length}
      >
        {dateOptions.map((date) => (
          <option key={date} value={date}>
            {formatYmd(date)}
          </option>
        ))}
      </select>
    </label>
  )

  const summaryLead =
    !isLoading && !error && matchups.length > 0 ? (
      <div className="grid gap-3 md:grid-cols-3">
        <SummaryCard label="Games" value={String(matchups.length)} detail={selectedDate ? formatYmd(selectedDate) : '—'} />
        <SummaryCard
          label="Biggest favorite"
          value={biggestFavorite ? biggestFavorite.favorite : '—'}
          detail={
            biggestFavorite
              ? `${pct(biggestFavorite.favoriteProb)} · fair ML ${formatOdds(biggestFavorite.favoriteOdds)}`
              : 'No games'
          }
        />
        <SummaryCard
          label="Closest game"
          value={closestGame ? `${closestGame.away} @ ${closestGame.home}` : '—'}
          detail={closestGame ? `${pct(closestGame.favoriteProb)} favorite edge` : 'No games'}
        />
      </div>
    ) : undefined

  const growTable = matchups.length >= TABLE_GROW_ROW_THRESHOLD

  return (
    <PageScrollLayout
      title="Daily matchups"
      subtitle={
        <>
          Ratings-only fair lines for the selected day&apos;s MLB schedule, including final, in-progress, and upcoming
          games. These probabilities use current team strength plus home-field advantage; sportsbook odds, starters,
          injuries, and lineups are not included yet.
        </>
      }
      headerExtra={datePicker}
      actions={<DataFreshness refetchKeys={REFETCH_KEYS} />}
      lead={summaryLead}
      growTable={growTable}
    >
      {isLoading && <p className="shrink-0 px-8 pt-3 text-slate-500">Loading matchups…</p>}
      {error && (
        <div className="mx-8 mt-3 shrink-0 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          <p className="font-medium">Could not load daily matchups.</p>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-rose-800">
            {error instanceof ApiError ? error.message : String(error)}
          </pre>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {matchups.length === 0 ? (
            <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-600">
              No MLB games found for this date in the local box scores or remaining schedule datasets.
            </div>
          ) : (
            <PageTableStack grow={growTable}>
              <ConditionalFormatProvider
                rows={sortedMatchups}
                columnKeys={MATCHUP_HEATMAP_COLUMNS}
                columnScales={MATCHUP_COLUMN_SCALES}
                getValue={getHeatmapValue}
              >
                <DataTableCard fillViewport={growTable} caption={`${sortedMatchups.length} games`}>
                <table className="table">
                  <thead>
                    <tr>
                    <SortableTableHeader
                      label="Time"
                      sortKey="time"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Matchup"
                      sortKey="matchup"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Away rating"
                      sortKey="awayRating"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Home rating"
                      sortKey="homeRating"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Away fair line"
                      sortKey="awayFairLine"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Home fair line"
                      sortKey="homeFairLine"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Favorite"
                      sortKey="favorite"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                    <SortableTableHeader
                      label="Status"
                      sortKey="status"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={setSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedMatchups.map((row) => (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap text-slate-500">{row.timeLabel}</td>
                      <td>
                        <div className="whitespace-nowrap font-medium text-slate-900">
                          {row.away} <span className="text-slate-400">@</span> {row.home}
                        </div>
                      </td>
                      <FormattedTableCell columnKey="awayRating" value={row.awayRating} className="font-mono">
                        {fmt3(row.awayRating)}
                      </FormattedTableCell>
                      <FormattedTableCell columnKey="homeRating" value={row.homeRating} className="font-mono">
                        {fmt3(row.homeRating)}
                      </FormattedTableCell>
                      <FormattedTableCell columnKey="awayProb" value={row.awayProb}>
                        <div className="font-medium">{pct(row.awayProb)}</div>
                        <div className="text-xs text-slate-500">{formatOdds(row.awayOdds)}</div>
                      </FormattedTableCell>
                      <FormattedTableCell columnKey="homeProb" value={row.homeProb}>
                        <div className="font-medium">{pct(row.homeProb)}</div>
                        <div className="text-xs text-slate-500">{formatOdds(row.homeOdds)}</div>
                      </FormattedTableCell>
                      <FormattedTableCell columnKey="favoriteProb" value={row.favoriteProb}>
                        <div className="font-medium text-slate-900">{row.favorite}</div>
                        <div className="text-xs text-slate-500">
                          {pct(row.favoriteProb)} · {formatOdds(row.favoriteOdds)}
                        </div>
                      </FormattedTableCell>
                      <td className="text-slate-500">{row.status}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
                </DataTableCard>
              </ConditionalFormatProvider>
            </PageTableStack>
          )}
        </>
      )}
    </PageScrollLayout>
  )
}

function SummaryCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 truncate text-lg font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{detail}</p>
    </div>
  )
}

type DailyGame = (RemainingScheduleRow | BoxScoreRow) & {
  source: 'box_score' | 'remaining'
}

function combineDailySchedule(boxScores: BoxScoreRow[], remaining: RemainingScheduleRow[]): DailyGame[] {
  const byGame = new Map<string, DailyGame>()
  for (const game of boxScores) {
    byGame.set(gameKey(game), { ...game, source: 'box_score' })
  }
  for (const game of remaining) {
    const key = gameKey(game)
    if (!byGame.has(key)) byGame.set(key, { ...game, source: 'remaining' })
  }
  return [...byGame.values()]
}

function gameKey(game: Pick<RemainingScheduleRow, 'GamePk' | 'Date' | 'Away_Team' | 'Home_Team'>): string {
  return String(game.GamePk ?? `${game.Date}-${game.Away_Team}-${game.Home_Team}`)
}

function sortMatchups(rows: MatchupRow[], key: SortKey, direction: SortDirection): MatchupRow[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const primary = compareSortValue(sortValue(a, key), sortValue(b, key))
    if (primary !== 0) return primary * multiplier
    return a.sortKey.localeCompare(b.sortKey) || a.away.localeCompare(b.away) || a.home.localeCompare(b.home)
  })
}

function defaultSortDirection(key: SortKey): SortDirection {
  switch (key) {
    case 'awayRating':
    case 'homeRating':
    case 'awayFairLine':
    case 'homeFairLine':
    case 'favorite':
      return 'desc'
    default:
      return 'asc'
  }
}

function sortValue(row: MatchupRow, key: SortKey): number | string {
  switch (key) {
    case 'time':
      return row.sortKey
    case 'matchup':
      return `${row.away} @ ${row.home}`
    case 'awayRating':
      return row.awayRating
    case 'homeRating':
      return row.homeRating
    case 'awayFairLine':
      return row.awayProb
    case 'homeFairLine':
      return row.homeProb
    case 'favorite':
      return row.favoriteProb
    case 'status':
      return row.status
  }
}

function compareSortValue(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b))
}

function buildRatingMap(rows: CurrentRatingRow[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const row of rows) {
    if (typeof row.Team !== 'string' || !Number.isFinite(row.Current)) continue
    map.set(row.Team, row.Current)
    map.set(normalizeTeamName(row.Team), row.Current)
  }
  return map
}

function ratingFor(team: string, map: Map<string, number>): number {
  const direct = map.get(team)
  if (direct !== undefined) return direct
  const aliased = TEAM_ALIASES[team] ?? normalizeTeamName(team)
  return map.get(aliased) ?? NaN
}

function normalizeTeamName(team: string): string {
  return team.trim().replace(/\s+/g, ' ')
}

function homeWinProbability(homeRating: number, awayRating: number): number {
  const x = (homeRating - awayRating + HOME_FIELD_ADVANTAGE) * WIN_PROB_COEFFICIENT
  return 1 / (1 + Math.exp(-x))
}

function fairAmericanOdds(probability: number): number {
  if (probability <= 0 || probability >= 1 || !Number.isFinite(probability)) return NaN
  if (probability >= 0.5) return Math.round((-100 * probability) / (1 - probability))
  return Math.round((100 * (1 - probability)) / probability)
}

function toYmd(value: unknown): string {
  if (typeof value !== 'string') return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1]! : ''
}

function localYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Prefer today; otherwise the schedule date closest to today (not season start). */
export function pickDefaultMatchupDate(dateOptions: readonly string[], todayYmd: string): string {
  if (dateOptions.includes(todayYmd)) return todayYmd
  const todayMs = ymdToSortableMs(todayYmd)
  let best = dateOptions[0]!
  let bestDiff = Infinity
  for (const ymd of dateOptions) {
    const diff = Math.abs(ymdToSortableMs(ymd) - todayMs)
    if (diff < bestDiff) {
      bestDiff = diff
      best = ymd
    }
  }
  return best
}

function ymdToSortableMs(ymd: string): number {
  return new Date(`${ymd}T12:00:00`).getTime()
}

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

function formatYmd(ymd: string): string {
  const date = new Date(`${ymd}T12:00:00`)
  return Number.isNaN(date.getTime()) ? ymd : DATE_FORMATTER.format(date)
}

function formatGameTime(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

function sortKeyForGame(game: DailyGame): string {
  if (typeof game.Scheduled_Time_UTC === 'string' && game.Scheduled_Time_UTC) return game.Scheduled_Time_UTC
  return `${game.Date}T99:99:99Z-${gameKey(game)}`
}

function statusLabel(game: DailyGame): string {
  if (game.source === 'box_score') {
    const awayRuns = typeof game.Away_Runs === 'number' ? game.Away_Runs : null
    const homeRuns = typeof game.Home_Runs === 'number' ? game.Home_Runs : null
    if (awayRuns != null && homeRuns != null) return `${game.Status} · ${awayRuns}-${homeRuns}`
  }
  return String(game.Status ?? '—')
}

function pct(value: number): string {
  return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '—'
}

function fmt3(value: number): string {
  return Number.isFinite(value) ? value.toFixed(3) : '—'
}

function formatOdds(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return value > 0 ? `+${value}` : String(value)
}
